import Vue from 'vue'
import { ActionTree } from 'vuex'
import * as types from '@vue-storefront/core/modules/checkout/store/checkout/mutation-types'
import RootState from '@vue-storefront/core/types/RootState'
import CheckoutState from '@vue-storefront/core/modules/checkout/types/CheckoutState'
import { Logger } from '@vue-storefront/core/lib/logger'
import { processURLAddress, timezoneTime } from "@vue-storefront/core/helpers";
import rootStore from '@vue-storefront/core/store'
import { customerAuthHeaders } from '@vue-storefront/core/modules/user/helpers/customerSession'
import config from 'config'
import checkoutInitialState from "@vue-storefront/core/modules/checkout/store/checkout/initial-state";
import updateObject from "@vue-storefront/core/scripts/utils/update-object";

const actions: ActionTree<CheckoutState, RootState> = {
  /**
   * Place order - send it to service worker queue
   * @param {Object} commit method
   * @param {Object} order order data to be send
   */
  async placeOrder ({ state, commit, dispatch }, { order }) {
    try {
      const result = await dispatch('order/placeOrder', order, {root: true})
      if (!result.resultCode || result.resultCode === 200) {
        let time = timezoneTime().getTime()
        Vue.prototype.$db.usersCollection.setItem('last-cart-bypass-ts', time)
        await rootStore.dispatch('cart/connect')
        await rootStore.dispatch('cart/clear', { clearOnServer: false })
        if (state.personalDetails.createAccount) {
          commit(types.CHECKOUT_DROP_PASSWORD)
        }
      }
      return result
    } catch (e) {
      Logger.error(e, 'checkout')()
    }
  },

  async getDeliveryTime ({ commit, dispatch }, { partner_code, location_code, shipping_method, cartId }) {

    let url = config.checkout.delivery_time_endpoint + '?partner_code='
      + partner_code + '&location_code=' + location_code + '&shipping_method=' + shipping_method + '&cartId=' + cartId;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json())
      .catch(error => {
        throw new Error('FetchError in request: ' + error.toString())
      })
      .finally(async () => {
        await dispatch('setShippingMethod', { partner_code, location_code, shipping_method, cartId })
      })
  },

  async setShippingMethod ({ commit }, { partner_code, location_code, shipping_method, cartId }) {
    let partners = rootStore.getters['partners/getPartners']
    if (partners.length < 1) {
      await rootStore.dispatch('partners/loadPartners')
    }
    let partner = rootStore.getters['partners/getPartnerByCode'](partner_code),
      userToken = '',
      customer = null,
      url = config.checkout.update_shipping_method_endpoint
    if (!partner || !partner.entity_id) {
      let groupPartner = rootStore.getters['partners/getGroupedByPartnerProducts'].find(group => group.partner.code === partner_code)
      partner = groupPartner ? groupPartner.partner : partner
    }
    if (!partner) {
      // Don't send a request with an undefined partner_id - that would either
      // crash here or silently corrupt the order's shipping-method record.
      throw new Error(`setShippingMethod: unable to resolve partner for code "${partner_code}"`)
    }
    if (rootStore.getters['user/isLoggedIn']) {
      customer = rootStore.state.user.current
      userToken = rootStore.getters['user/getUserToken'] ? rootStore.getters['user/getUserToken'] : rootStore.state.user.token
    }
    return fetch(
      processURLAddress(url),
      {
        method: 'POST',
        headers: Object.assign({
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }, customerAuthHeaders(userToken)), // K2-3138: not in the URL
        body: JSON.stringify({
          partner_id: partner.entity_id ? partner.entity_id : partner.id,
          customer_id: (customer && customer['id']) ? customer['id'] : null,
          location_code,
          shipping_method,
          cart_id: cartId
        })
    })
      .then(resp => resp.json())
      .catch(error => {
        throw new Error('FetchError for set shipping method request: ' + error.toString())
      })
  },

  saveCutleryQty ({ commit }, cutleryQty) {
    commit(types.CHECKOUT_SAVE_CUTLERY_QTY, cutleryQty)
  },

  setModifiedAt ({ commit }, timestamp) {
    commit(types.CHECKOUT_SET_MODIFIED_AT, timestamp)
  },
  savePersonalDetails ({ commit }, personalDetails) {
    // todo: create and move perdonal details vuex
    commit(types.CHECKOUT_SAVE_PERSONAL_DETAILS, personalDetails)
  },
  async saveShippingDetails ({ commit }, shippingDetails) {
    // todo: move to shipping vuex
    commit(types.CHECKOUT_SAVE_SHIPPING_DETAILS, shippingDetails)
  },
  getGuestShippingDetails ({ state, commit }) {
    const localStorageGuestAddresses = JSON.parse(localStorage.getItem('shop/user/guest-addresses'));

    if (localStorageGuestAddresses && localStorageGuestAddresses[0]) {
      const address = localStorageGuestAddresses[0];
      commit(types.CHECKOUT_SAVE_SHIPPING_DETAILS, updateObject(state.shippingDetails, {
        firstName: address.firstname,
        lastName: address.lastname,
        country: address.country,
        state: address.state,
        city: address.city,
        longitude: address.longitude,
        latitude: address.latitude,
        api_details: address.api_details,
        street: address.street,
        house: address.house,
        apartment: address.apartment,
        postcode: (address.postcode && address.postcode === '') ? '' : address.postcode,
        comment: (address.comment && address.comment === '') ? '' : address.comment,
        shippingMethod: state.shippingDetails.shippingMethod,
        shippingCarrier: state.shippingDetails.shippingCarrier
      }))
    } else {
      commit(types.CHECKOUT_SAVE_SHIPPING_DETAILS, state.shippingDetails)
    }
  },
  saveDeliveryTime ({ commit }, deliveryTime) {
    commit(types.CHECKOUT_SAVE_DELIVERYTIME, deliveryTime)
  },
  savePaymentDetails ({ commit }, paymentDetails) {
    // todo: move to payment vuex
    commit(types.CHECKOUT_SAVE_PAYMENT_DETAILS, paymentDetails)
  },
  load ({ commit }) {
    Vue.prototype.$db.checkoutFieldsCollection.getItem('cutlery-qty', (err, details) => {
      if (err) throw new Error(err)
      if (details) {
        commit(types.CHECKOUT_SAVE_CUTLERY_QTY, details)
      }
    })

    Vue.prototype.$db.checkoutFieldsCollection.getItem('personal-details', (err, details) => {
      if (err) throw new Error(err)
      if (details) {
        commit(types.CHECKOUT_LOAD_PERSONAL_DETAILS, details)
      }
    })
    Vue.prototype.$db.checkoutFieldsCollection.getItem('shipping-details', (err, details) => {
      if (err) throw new Error(err)
      if (details) {
        commit(types.CHECKOUT_LOAD_SHIPPING_DETAILS, details)
      }
    })
    Vue.prototype.$db.checkoutFieldsCollection.getItem('payment-details', (err, details) => {
      if (err) throw new Error(err)
      if (details) {
        commit(types.CHECKOUT_LOAD_PAYMENT_DETAILS, details)
      }
    })
    Vue.prototype.$db.checkoutFieldsCollection.getItem('delivery-time', (err, details) => {
      if (err) throw new Error(err)
      if (details) {
        commit(types.CHECKOUT_SAVE_DELIVERYTIME, details)
      }
    })
  },
  updatePropValue ({ commit }, payload) {
    commit(types.CHECKOUT_UPDATE_PROP_VALUE, payload)
  },
  setThankYouPage ({ commit }, payload) {
    commit(types.CHECKOUT_SET_THANKYOU, payload)
  },
  setComment({ commit }, payload) {
    commit(types.CHECKOUT_SAVE_COMMENT, payload)
    localStorage.setItem('shop/checkoutFieldValues/comment', payload);
  },
  saveFirstUserShippingDetails({ commit }, payload) {
    commit(types.CHECKOUT_SAVE_FIRST_USER_SHIPPING_DETAILS, payload)
  },
  setPaymentFailedPage ({ commit }, payload) {
    commit(types.CHECKOUT_SET_PAYMENT_FAILED, payload)
  },
  setAddNewAddress({ commit }, payload) {
    commit(types.CHECKOUT_SAVE_ADDNEWADDRESS, payload)
  },
  clearCheckoutDetails({ commit }) {
    commit(types.CHECKOUT_CLEAR_DETAILS, {
      personalDetails: checkoutInitialState.personalDetails,
      shippingDetails: checkoutInitialState.shippingDetails,
      paymentDetails: checkoutInitialState.paymentDetails
    })
  }
}

export default actions
